import React from 'react';
import type { Pet } from '../../types/sanity';

interface Filters {
  mainCategory?: string[];
  age?: string[];
  gender?: string[];
  size?: string[];
  tags?: string[];
  status?: string[];
}

interface PetFiltersProps {
  filters: Filters;
  setFilters: (filters: Filters) => void;
  pets: Pet[];
}

export function PetFilters({ filters, setFilters, pets }: PetFiltersProps) {
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="heading-5 text-white dark:text-white high-contrast:text-white">Filtry</h3>
        <button
          onClick={() => setFilters({})}
          className="body-small text-white/90 hover:text-white no-margin"
        >
          Wyczyść wszystkie
        </button>
      </div>
      
      <div>
        <h4 className="heading-6 mb-3 text-white dark:text-white high-contrast:text-white">Gatunek</h4>
        <div className="space-y-2">
          {[
            { value: 'dog', label: 'Pies' },
            { value: 'cat', label: 'Kot' }
          ].map(({ value, label }) => (
            <label key={value} className="flex items-center gap-2 text-white dark:text-white high-contrast:text-white">
              <input
                type="checkbox"
                checked={filters.mainCategory?.includes(value) || false}
                onChange={(e) => {
                  setFilters({
                    ...filters,
                    mainCategory: e.target.checked
                      ? [...(filters.mainCategory || []), value]
                      : (filters.mainCategory || []).filter(c => c !== value)
                  });
                }}
                className="rounded border-white/40 text-white focus:ring-white focus:ring-2 bg-transparent"
              />
              <span className="body-medium no-margin">{label}</span>
            </label>
          ))}
        </div>
      </div>
      
      <div>
        <h4 className="heading-6 mb-3 text-white dark:text-white high-contrast:text-white">Wiek</h4>
        <div className="space-y-2">
          {['young', 'adult', 'senior'].map((age) => (
            <label key={age} className="flex items-center gap-2 text-white dark:text-white high-contrast:text-white">
              <input
                type="checkbox"
                checked={filters.age?.includes(age) || false}
                onChange={(e) => {
                  setFilters({
                    ...filters,
                    age: e.target.checked
                      ? [...(filters.age || []), age]
                      : (filters.age || []).filter(a => a !== age)
                  });
                }}
                className="rounded border-white/40 text-white focus:ring-white focus:ring-2 bg-transparent"
              />
              <span className="body-medium no-margin">
                {age === 'young' ? 'Młody (0-2 lata)' :
                 age === 'adult' ? 'Dorosły (3-8 lat)' :
                 'Senior (9+ lat)'}
              </span>
            </label>
          ))}
        </div>
      </div>
      
      <div>
        <h4 className="heading-6 mb-3 text-white dark:text-white high-contrast:text-white">Płeć</h4>
        <div className="space-y-2">
          {['male', 'female'].map((gender) => (
            <label key={gender} className="flex items-center gap-2 text-white dark:text-white high-contrast:text-white">
              <input
                type="checkbox"
                checked={filters.gender?.includes(gender) || false}
                onChange={(e) => {
                  setFilters({
                    ...filters,
                    gender: e.target.checked
                      ? [...(filters.gender || []), gender]
                      : (filters.gender || []).filter(g => g !== gender)
                  });
                }}
                className="rounded border-white/40 text-white focus:ring-white focus:ring-2 bg-transparent"
              />
              <span className="body-medium no-margin">
                {gender === 'male' ? 'Samiec' : 'Samica'}
              </span>
            </label>
          ))}
        </div>
      </div>
      
      <div>
        <h4 className="heading-6 mb-3 text-white dark:text-white high-contrast:text-white">Rozmiar</h4>
        <div className="space-y-2">
          {[
            { value: 'mini', label: 'Miniaturka' },
            { value: 'small', label: 'Mały' },
            { value: 'medium', label: 'Średni' },
            { value: 'large', label: 'Duży' }
          ].map(({ value, label }) => (
            <label key={value} className="flex items-center gap-2 text-white dark:text-white high-contrast:text-white">
              <input
                type="checkbox"
                checked={filters.size?.includes(value) || false}
                onChange={(e) => {
                  setFilters({
                    ...filters,
                    size: e.target.checked
                      ? [...(filters.size || []), value]
                      : (filters.size || []).filter(s => s !== value)
                  });
                }}
                className="rounded border-white/40 text-white focus:ring-white focus:ring-2 bg-transparent"
              />
              <span className="body-medium no-margin">{label}</span>
            </label>
          ))}
        </div>
      </div>

      <div>
        <h4 className="heading-6 mb-3 text-white dark:text-white high-contrast:text-white">Status adopcji</h4>
        <div className="space-y-2">
          {[
            { value: 'available', label: 'Do adopcji' },
            { value: 'reserved', label: 'Zarezerwowany' },
            { value: 'not_adoptable', label: 'Nie do adopcji' }
          ].map(({ value, label }) => (
            <label key={value} className="flex items-center gap-2 text-white dark:text-white high-contrast:text-white">
              <input
                type="checkbox"
                checked={filters.status?.includes(value) || false}
                onChange={(e) => {
                  setFilters({
                    ...filters,
                    status: e.target.checked
                      ? [...(filters.status || []), value]
                      : (filters.status || []).filter(s => s !== value)
                  });
                }}
                className="rounded border-white/40 text-white focus:ring-white focus:ring-2 bg-transparent"
              />
              <span className="body-medium no-margin">{label}</span>
            </label>
          ))}
        </div>
      </div>

      <div>
        <h4 className="heading-6 mb-3 text-white dark:text-white high-contrast:text-white">Cechy</h4>
        <div className="space-y-2">
          {pets.reduce((uniqueTags, pet) => {
            if (pet.tags) {
              pet.tags.forEach(tag => {
                if (!uniqueTags.some(t => t.slug.current === tag.slug.current)) {
                  uniqueTags.push(tag);
                }
              });
            }
            return uniqueTags;
          }, [] as Pet['tags']).map((tag) => (
            <label key={tag.slug.current} className="flex items-center gap-2 text-white dark:text-white high-contrast:text-white">
              <input
                type="checkbox"
                checked={filters.tags?.includes(tag.slug.current) || false}
                onChange={(e) => {
                  setFilters({
                    ...filters,
                    tags: e.target.checked
                      ? [...(filters.tags || []), tag.slug.current]
                      : (filters.tags || []).filter(t => t !== tag.slug.current)
                  });
                }}
                className="rounded border-white/40 text-white focus:ring-white focus:ring-2 bg-transparent"
              />
              <span className="body-medium no-margin">{tag.label}</span>
            </label>
          ))}
        </div>
      </div>
    </div>
  );
}